import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Layout } from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { CAREER_STAGES, CAREER_AUDIENCES, ENGAGEMENT_PROCESS } from "@/data/content";
import { api } from "@/lib/api";
import { ArrowRight } from "lucide-react";

export default function CareerAdvisory() {
  const [services, setServices] = useState([]);
  useEffect(() => {
    api.get("/services", { params: { category: "career_advisory" } })
      .then((r) => setServices(r.data)).catch(() => {});
  }, []);

  return (
    <Layout>
      <section className="pt-36 pb-16 lg:pt-44" data-testid="career-hero">
        <div className="container-editorial">
          <Reveal>
            <div className="eyebrow">Career Advisory & Interview Preparation</div>
          </Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-display text-5xl md:text-7xl mt-6 leading-[1.02] tracking-tight max-w-5xl">
              A four‑stage <span className="italic text-gold">career transformation</span> framework — for professionals authoring the next chapter.
            </h1>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="max-w-2xl mt-10 text-lg text-slate2 leading-relaxed">
              Every module is customizable — from foundational positioning for early careers to high-impact strategic
              advisory for executives — ensuring you get exactly the guidance required for your next tier.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FOUR-STAGE FRAMEWORK */}
      <section className="py-16 lg:py-24 bg-charcoal text-ivory relative overflow-hidden grain grain-dark" data-testid="career-framework">
        <div className="container-editorial relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-14">
            <div className="lg:col-span-6">
              <div className="eyebrow text-gold">The Framework</div>
              <h2 className="font-display text-4xl md:text-5xl mt-6 leading-tight tracking-tight">
                Four-Stage Career Transformation.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
            {CAREER_STAGES.map((s, i) => (
              <Reveal key={s.step} delay={i * 0.08}>
                <div className="panel-dark h-full relative" data-testid={`career-stage-${s.step}`}>
                  <div className="font-display text-gold text-6xl leading-none">{s.step}</div>
                  <div className="font-display text-2xl mt-4">{s.title}</div>
                  <p className="text-sm mt-3 leading-relaxed text-[#D8D5CE]">{s.body}</p>
                  {i < 3 && <div className="hidden lg:block absolute top-8 -right-6 w-6 h-px bg-gold/40" />}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ENGAGEMENT PROCESS */}
      <section className="py-24" data-testid="career-process">
        <div className="container-editorial">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5">
              <div className="eyebrow">Engagement Process</div>
              <h2 className="font-display text-4xl md:text-5xl mt-6 leading-tight tracking-tight">
                A structured journey — <span className="italic text-gold">deliberately sequenced</span>.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <p className="text-lg text-slate2 leading-relaxed">
                A structured journey designed to move clients from initial discovery and technical evaluation through
                to strategic advisory, asset optimization, interview simulation, and sustainable career acceleration.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {ENGAGEMENT_PROCESS.map((s, i) => (
              <Reveal key={s.title} delay={i * 0.05}>
                <div
                  className="border border-[#E0DCD3] bg-white p-6 lg:p-8 h-full"
                  data-testid={`career-process-${i + 1}`}
                >
                  <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-gold">
                    Step {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="font-display text-2xl mt-3">{s.title}</div>
                  <p className="mt-3 text-sm text-slate2 leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* AUDIENCE — BENTO */}
      <section className="py-24 bg-ivory-surface relative overflow-hidden grain" data-testid="career-audience">
        <div className="container-editorial relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-5">
              <div className="eyebrow">Targeted Career Milestone Support</div>
              <h2 className="font-display text-4xl md:text-5xl mt-6 leading-tight tracking-tight">
                Different stages. The <span className="italic text-gold">same</span> seriousness.
              </h2>
            </div>
            <div className="lg:col-span-6 lg:col-start-7">
              <p className="text-lg text-slate2 leading-relaxed">
                Our services are categorized by experience level to provide targeted interventions. Every module is
                customizable — you only pay for the guidance required to unlock your next tier.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 lg:grid-cols-6 gap-4">
            {CAREER_AUDIENCES.map((a) => {
              const span =
                a.size === "large"
                  ? "md:col-span-4 lg:col-span-4 md:row-span-2"
                  : "md:col-span-2 lg:col-span-2";
              return (
                <div
                  key={a.slug}
                  className={`border border-[#E0DCD3] bg-white p-8 lg:p-10 hover:border-gold transition-colors ${span}`}
                  data-testid={`career-audience-${a.slug}`}
                >
                  <div className="eyebrow">{a.slug === "leadership" ? "Marquee" : "Cohort"}</div>
                  <div className={`font-display mt-3 leading-tight ${a.size === "large" ? "text-4xl md:text-5xl" : "text-2xl"}`}>{a.title}</div>
                  <p className="mt-4 text-slate2 text-sm leading-relaxed">{a.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICE CATALOGUE — REQUESTABLE */}
      <section className="py-24" data-testid="career-catalogue">
        <div className="container-editorial">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
            <div className="lg:col-span-6">
              <div className="eyebrow">Service Offerings</div>
              <h2 className="font-display text-4xl md:text-5xl mt-6 leading-tight tracking-tight">
                Six engagements — each one whole.
              </h2>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {services.map((s, i) => (
              <Reveal key={s.slug} delay={i * 0.04}>
                <div
                  className="border border-[#E0DCD3] bg-white p-8 lg:p-10 flex flex-col h-full"
                  data-testid={`career-service-${s.slug}`}
                >
                  <div className="font-display text-2xl leading-tight">{s.title}</div>
                  {s.tagline && <div className="font-display italic text-lg text-gold mt-2">{s.tagline}</div>}
                  <p className="text-sm text-slate2 mt-4 leading-relaxed">{s.description}</p>
                  {s.features?.length ? (
                    <ul className="mt-4 space-y-1 text-sm text-slate2">
                      {s.features.map((f) => (
                        <li key={f}>· {f}</li>
                      ))}
                    </ul>
                  ) : null}
                  {s.audience && (
                    <div className="mt-4 font-mono text-[10px] uppercase tracking-[0.22em] text-slate2/80">
                      For: {s.audience}
                    </div>
                  )}
                  <div className="mt-6 pt-6 hairline flex items-center justify-between">
                    <div className="font-mono text-[10px] uppercase tracking-[0.24em] text-slate2">
                      {s.duration_minutes ? `${s.duration_minutes} min · Private engagement` : "By arrangement"}
                    </div>
                    <Link to={`/book?service=${s.slug}`} className="btn-ghost" data-testid={`career-book-${s.slug}`}>
                      Request <ArrowRight size={12} />
                    </Link>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-charcoal text-ivory">
        <div className="container-editorial flex flex-col lg:flex-row lg:items-end gap-10 justify-between">
          <h2 className="font-display text-4xl md:text-5xl leading-tight tracking-tight max-w-3xl text-ivory">
            Submit your CV for an initial review and schedule your <span className="italic text-gold">discovery call</span>.
          </h2>
          <Link to="/book" className="btn-primary btn-on-dark" data-testid="career-final-book">
            Request an appointment
          </Link>
        </div>
      </section>
    </Layout>
  );
}
